import type {Object3D} from 'three'
import {
  areAssemblyNodeIdentityEquivalent,
} from '../assembly/utils/AssemblyNodePath'
import {readComponentInstanceAssemblyNodeId} from '../assembly/component-instance'
import {ImportedModelObjectMetadataService} from '../imported-model/metadata'
import {
  collectImportedModelTopologyRepositories,
} from '../imported-model/topology/ImportedModelTopologyRepository'
import {areImportedBodyBindingIdsEquivalent} from '../imported-model/utils'
import {
  LinearComponentPatternReferenceDescriptorKind,
  type LinearComponentPatternReferenceDescriptor,
  type LinearComponentPatternState,
} from './LinearComponentPatternTypes'

/** 线性阵列状态中保存普通场景引用的字段。 */
const LINEAR_COMPONENT_PATTERN_REFERENCE_OBJECT_ID_KEYS = [
  'direction1ReferenceObjectId',
  'direction1RotationAxisObjectId',
  'direction1ReferenceGeometryObjectId',
  'direction1SelectedReferenceObjectId',
  'direction2ReferenceObjectId',
  'direction2ReferenceGeometryObjectId',
  'direction2SelectedReferenceObjectId',
] as const satisfies readonly (keyof LinearComponentPatternState)[]

/** 线性阵列状态中保存稳定引用描述符的字段。 */
const LINEAR_COMPONENT_PATTERN_REFERENCE_DESCRIPTOR_KEYS = [
  'direction1ReferenceDescriptor',
  'direction1RotationAxisDescriptor',
  'direction1ReferenceGeometryDescriptor',
  'direction1SelectedReferenceDescriptor',
  'direction2ReferenceDescriptor',
  'direction2ReferenceGeometryDescriptor',
  'direction2SelectedReferenceDescriptor',
] as const satisfies readonly (keyof LinearComponentPatternState)[]

/**
 * 创建旧子树线性阵列删除影响计划。
 *
 * @param input 当前阵列记录与旧子树运行时范围。
 * @returns 冻结的受影响阵列计划。
 */
export function createLinearComponentPatternSubtreeRemovalImpactPlan(
  input: PuppyCAD.CreateLinearComponentPatternSubtreeRemovalImpactPlanInput,
): PuppyCAD.LinearComponentPatternSubtreeRemovalImpactPlan {
  const defaultOwnerAssemblyNodeId = input.defaultOwnerAssemblyNodeId.trim()
  if (!defaultOwnerAssemblyNodeId) {
    throw new Error('线性阵列旧子树影响分析缺少当前文档根 owner。')
  }

  const subtreeObjects = collectSubtreeObjects(input.subtreeRootObjects)
  const subtreeAssemblyNodeIds = collectSubtreeAssemblyNodeIds(
    subtreeObjects,
    input.assemblyNodeIds,
  )
  const subtreeBodyBindingIds = collectSubtreeBodyBindingIds(
    input.subtreeRootObjects,
    subtreeObjects,
  )

  const isAssemblyNodeInSubtree = (assemblyNodeId: string | null | undefined): boolean => {
    const normalizedAssemblyNodeId = assemblyNodeId?.trim() ?? ''
    return normalizedAssemblyNodeId.length > 0 && subtreeAssemblyNodeIds.some(
      candidate => areAssemblyNodeIdentityEquivalent(candidate, normalizedAssemblyNodeId),
    )
  }
  const isBodyBindingInSubtree = (bodyBindingId: string | null | undefined): boolean => {
    const normalizedBodyBindingId = bodyBindingId?.trim() ?? ''
    return normalizedBodyBindingId.length > 0 && subtreeBodyBindingIds.some(
      candidate => areImportedBodyBindingIdsEquivalent(candidate, normalizedBodyBindingId),
    )
  }
  const isObjectReferenceInSubtree = (objectId: string | null | undefined): boolean => {
    const normalizedObjectId = objectId?.trim() ?? ''
    if (!normalizedObjectId) {
      return false
    }
    const resolvedObject = input.resolveSceneObjectById(normalizedObjectId)
    return Boolean(resolvedObject && subtreeObjects.has(resolvedObject))
      || isAssemblyNodeInSubtree(normalizedObjectId)
  }

  const removedRecords = input.records.flatMap((record) => {
    const ownerAssemblyNodeId = record.ownerAssemblyNodeId?.trim()
      || defaultOwnerAssemblyNodeId
    const state = record.definition?.state
    const affected = isAssemblyNodeInSubtree(ownerAssemblyNodeId)
      || Boolean(state && state.componentObjectIds.some(isObjectReferenceInSubtree))
      || Boolean(state && LINEAR_COMPONENT_PATTERN_REFERENCE_OBJECT_ID_KEYS.some(key => (
        isObjectReferenceInSubtree(state[key] as string | null | undefined)
        || isBodyBindingInSubtree(state[key] as string | null | undefined)
      )))
      || Boolean(state && LINEAR_COMPONENT_PATTERN_REFERENCE_DESCRIPTOR_KEYS.some(key => (
        isReferenceDescriptorInSubtree(
          state[key] as LinearComponentPatternReferenceDescriptor | null | undefined,
          isObjectReferenceInSubtree,
          isAssemblyNodeInSubtree,
          isBodyBindingInSubtree,
        )
      )))

    return affected
      ? [{
          groupObjectId: record.groupObjectId,
          featureId: record.id,
          ownerAssemblyNodeId,
          displayName: record.displayName,
        }]
      : []
  })

  return {removedRecords}
}

/**
 * 收集旧子树中的真实 Object3D。
 *
 * @param roots 旧子树根。
 * @returns 按对象引用去重的子树对象集合。
 */
function collectSubtreeObjects(roots: readonly Object3D[]): ReadonlySet<Object3D> {
  const objects = new Set<Object3D>()
  roots.forEach(root => root.traverse(object => objects.add(object)))
  return objects
}

/**
 * 收集旧子树运行时携带的 occurrence 身份。
 *
 * @param objects 旧子树对象集合。
 * @param explicitAssemblyNodeIds 业务层已经确定的 occurrence。
 * @returns 未按字符串去重、但会用统一等价规则比较的 occurrence 列表。
 */
function collectSubtreeAssemblyNodeIds(
  objects: ReadonlySet<Object3D>,
  explicitAssemblyNodeIds: readonly string[],
): readonly string[] {
  const result = explicitAssemblyNodeIds
    .map(value => value.trim())
    .filter(value => value.length > 0)
  objects.forEach((object) => {
    const metadata = ImportedModelObjectMetadataService.readOwnMetadata(object)
    const candidates = [
      readComponentInstanceAssemblyNodeId(object),
      metadata?.assemblyNodeId,
      metadata?.selectionTarget?.assemblyNodeId,
      metadata?.hierarchy?.meta?.assemblyNodeId,
      ...Object.values(metadata?.bodyBindingSelectionTargetsById ?? {})
        .map(target => target.assemblyNodeId),
    ]
    candidates.forEach((candidate) => {
      const normalizedCandidate = candidate?.trim() ?? ''
      if (normalizedCandidate) {
        result.push(normalizedCandidate)
      }
    })
  })
  return result
}

/**
 * 收集旧子树实际拥有的 imported body occurrence。
 *
 * 共享 repository 可能同时持有兄弟节点记录，因此只接纳 visible mesh 位于旧子树的记录。
 *
 * @param roots 旧子树根。
 * @param objects 旧子树对象集合。
 * @returns bodyBindingId 列表。
 */
function collectSubtreeBodyBindingIds(
  roots: readonly Object3D[],
  objects: ReadonlySet<Object3D>,
): readonly string[] {
  const result = new Set<string>()
  objects.forEach((object) => {
    const metadata = ImportedModelObjectMetadataService.readOwnMetadata(object)
    Object.keys(metadata?.bodyBindingSelectionTargetsById ?? {}).forEach(bodyBindingId => {
      const normalizedBodyBindingId = bodyBindingId.trim()
      if (normalizedBodyBindingId) {
        result.add(normalizedBodyBindingId)
      }
    })
  })
  roots.forEach((root) => {
    collectImportedModelTopologyRepositories(root).forEach((repository) => {
      repository.listBodyRecords().forEach((record) => {
        if (objects.has(record.visibleMesh)) {
          result.add(record.bodyBindingId)
        }
      })
    })
  })
  return [...result]
}

/**
 * 判断单个稳定引用描述符是否依赖旧子树。
 *
 * @param descriptor 阵列引用描述符。
 * @param isObjectReferenceInSubtree 场景对象引用判断器。
 * @param isAssemblyNodeInSubtree occurrence 判断器。
 * @param isBodyBindingInSubtree body occurrence 判断器。
 * @returns 引用依赖旧子树时返回 `true`。
 */
function isReferenceDescriptorInSubtree(
  descriptor: LinearComponentPatternReferenceDescriptor | null | undefined,
  isObjectReferenceInSubtree: (objectId: string | null | undefined) => boolean,
  isAssemblyNodeInSubtree: (assemblyNodeId: string | null | undefined) => boolean,
  isBodyBindingInSubtree: (bodyBindingId: string | null | undefined) => boolean,
): boolean {
  if (!descriptor) {
    return false
  }
  if (descriptor.kind === LinearComponentPatternReferenceDescriptorKind.SceneObject) {
    return isObjectReferenceInSubtree(descriptor.objectId)
  }
  return isObjectReferenceInSubtree(descriptor.hostObjectId)
    || isBodyBindingInSubtree(descriptor.hostObjectId)
    || isAssemblyNodeInSubtree(descriptor.assemblyTopologyRef?.assemblyNodeId)
    || isBodyBindingInSubtree(descriptor.assemblyTopologyRef?.bodyBindingId)
}
